<template>
  <div class="self-order-stop-button">
    <v-btn
      :color="stopped ? 'error' : 'success'"
      :outlined="!stopped"
      :small="compact"
      :loading="isLoading"
      :disabled="isLoading"
      depressed
      class="self-order-stop-button__btn"
      @click="isDialogOpen = true"
    >
      <v-icon
        :small="compact"
        left
      >
        {{ stopped ? icons.mdiPauseCircle : icons.mdiPlayCircleOutline }}
      </v-icon>
      {{ stopped ? 'オーダーストップ中' : 'オーダー受付中' }}
    </v-btn>
    <div
      v-if="!compact"
      class="text-caption text--secondary mt-1"
    >
      {{ statusNote }}
    </div>

    <v-dialog
      v-model="isDialogOpen"
      max-width="420"
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ stopped ? 'オーダーを再開しますか？' : 'オーダーストップしますか？' }}
        </v-card-title>
        <v-card-text>
          <template v-if="stopped">
            モバイルオーダーからの注文受付を再開します。
            <span v-if="autoStopped">自動停止時刻({{ autoStopAt }})を過ぎていますが、本日の営業中は受け付けるようになります。</span>
          </template>
          <template v-else>
            モバイルオーダーからの注文をこれ以上受け付けなくなります(メニューの閲覧・会計依頼はできます)。
            翌営業日には自動的に受付中に戻ります。
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="isDialogOpen = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            :color="stopped ? 'success' : 'error'"
            :loading="isSubmitting"
            depressed
            @click="toggle"
          >
            {{ stopped ? '再開する' : 'オーダーストップ' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ref, computed, onMounted, getCurrentInstance,
} from '@vue/composition-api'
import { mdiPauseCircle, mdiPlayCircleOutline } from '@mdi/js'
import SelfOrderStopApi from '@/api/waiter/SelfOrderStop'

// モバイルオーダーのオーダーストップ切替ボタン(店舗単位)。
// Waiterアプリバー(compact)とセルフオーダー設定画面(通常)の両方で使う。状態はマウント時にAPIから取得する
export default {
  name: 'SelfOrderStopButton',
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const isLoading = ref(false)
    const isSubmitting = ref(false)
    const isDialogOpen = ref(false)
    const stopped = ref(false)
    const manualStatus = ref(null)
    const autoStopAt = ref(null)
    const autoStopped = ref(false)
    const resumesAt = ref(null)

    const applyState = data => {
      stopped.value = !!data.stopped
      manualStatus.value = data.manualStatus ?? null
      autoStopAt.value = data.autoStopAt ?? null
      autoStopped.value = !!data.autoStopped
      resumesAt.value = data.resumesAt ?? null
    }

    // "9/8 16:00" 形式(翌営業日の開店時刻)
    const formatResumesAt = () => {
      if (!resumesAt.value) return ''
      const d = new Date(resumesAt.value)
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')

      return `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mm}`
    }

    // 状態は「いつ・なぜ止まっているか」「いつ戻るか」を具体的な時刻で示す(リロードで最新化)
    const statusNote = computed(() => {
      if (stopped.value) {
        const reason = manualStatus.value === 'stopped'
          ? '手動で停止中です。'
          : `自動停止時刻(${autoStopAt.value})を過ぎたため停止中です。`

        return `${reason}${formatResumesAt()}(翌営業日の開店)に受付中へ戻ります`
      }
      if (autoStopAt.value) {
        return manualStatus.value === 'resumed' && autoStopped.value
          ? `手動で再開中です。本日はこれ以上自動停止しません(次回の自動停止は翌営業日の ${autoStopAt.value})`
          : `本日 ${autoStopAt.value} に自動でオーダーストップします`
      }

      return '自動停止時刻は未設定です(ボタンで止めない限り受付中)'
    })

    const fetchState = async () => {
      isLoading.value = true
      try {
        const res = await SelfOrderStopApi.getSelfOrderStop()
        applyState(res.data)
      } catch (error) {
        vm.$toast.error('オーダーストップの状態取得に失敗しました')
      } finally {
        isLoading.value = false
      }
    }

    const toggle = async () => {
      isSubmitting.value = true
      try {
        const res = await SelfOrderStopApi.updateSelfOrderStop({ stopped: !stopped.value })
        applyState(res.data)
        isDialogOpen.value = false
        vm.$toast.success(stopped.value ? 'オーダーストップしました' : 'オーダーを再開しました')
        emit('change', res.data)
      } catch (error) {
        vm.$toast.error('オーダーストップの切替に失敗しました')
      } finally {
        isSubmitting.value = false
      }
    }

    onMounted(fetchState)

    return {
      isLoading,
      isSubmitting,
      isDialogOpen,
      stopped,
      autoStopAt,
      autoStopped,
      statusNote,
      toggle,
      refresh: fetchState,
      icons: { mdiPauseCircle, mdiPlayCircleOutline },
    }
  },
}
</script>
